import { apiJson } from "@/lib/api/client";
import type {
  ConfirmRequest,
  ParametersRequest,
  SessionStatus,
  StartSessionRequest,
  StartSessionResponse,
} from "@/lib/api/types";

/**
 * Typed wrappers around the eight POST/GET/DELETE session endpoints.
 * Field names + URL paths must match `super_agent.api` exactly.
 */

export async function startSession(
  body: StartSessionRequest,
  signal?: AbortSignal,
): Promise<StartSessionResponse> {
  return apiJson<StartSessionResponse, StartSessionRequest>("/sessions", {
    method: "POST",
    body,
    signal,
  });
}

export async function getSession(
  sessionId: string,
  signal?: AbortSignal,
): Promise<SessionStatus> {
  return apiJson<SessionStatus>(`/sessions/${sessionId}`, { signal });
}

export async function submitRecommendation(
  sessionId: string,
  body: ConfirmRequest,
  signal?: AbortSignal,
): Promise<SessionStatus> {
  return apiJson<SessionStatus, ConfirmRequest>(
    `/sessions/${sessionId}/recommendation`,
    { method: "POST", body, signal },
  );
}

export async function submitParameters(
  sessionId: string,
  body: ParametersRequest,
  signal?: AbortSignal,
): Promise<SessionStatus> {
  return apiJson<SessionStatus, ParametersRequest>(
    `/sessions/${sessionId}/parameters`,
    { method: "POST", body, signal },
  );
}

export async function submitDeployConfirmation(
  sessionId: string,
  body: ConfirmRequest,
  signal?: AbortSignal,
): Promise<SessionStatus> {
  return apiJson<SessionStatus, ConfirmRequest>(
    `/sessions/${sessionId}/deploy-confirmation`,
    { method: "POST", body, signal },
  );
}

export interface ExplainRequest {
  step: "recommendation" | "parameters" | "plan";
}

export interface ExplainResponse {
  step: "recommendation" | "parameters" | "plan";
  text: string;
  model: string | null;
}

export async function explainStep(
  sessionId: string,
  body: ExplainRequest,
  signal?: AbortSignal,
): Promise<ExplainResponse> {
  return apiJson<ExplainResponse, ExplainRequest>(
    `/sessions/${sessionId}/explain`,
    { method: "POST", body, signal },
  );
}

export interface ChatTurn {
  q: string;
  a: string;
}

export interface ExtractScenarioRequest {
  turns: ChatTurn[];
}

export interface ExtractScenarioResponse {
  scenario: import("@/lib/api/types").ScenarioBrief;
}

export async function extractScenario(
  body: ExtractScenarioRequest,
  signal?: AbortSignal,
): Promise<ExtractScenarioResponse> {
  return apiJson<ExtractScenarioResponse, ExtractScenarioRequest>(
    "/scenario/extract",
    { method: "POST", body, signal },
  );
}

export async function cancelSession(
  sessionId: string,
  signal?: AbortSignal,
): Promise<void> {
  await apiJson<void>(`/sessions/${sessionId}`, {
    method: "DELETE",
    signal,
  });
}
